"use client";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Menu } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import logout from "@/lib/logout";
import SDK from "@/lib/sdk";
import useUserStore from "@/store/user-store";

function Navigation({ onNavigate }: { onNavigate?: () => void }) {
  const router = useRouter()
  const pathname = usePathname();

  const logUserOut = async () => {
    if (onNavigate) {
      onNavigate()
    }

    await logout();

    router.push('/')
    router.refresh()
  };

  return (
    <>
      <Link
        className={pathname === "/user/calendar" ? "text-primary-text" : ""}
        href="/user/calendar"
        onClick={onNavigate}
      >
        Reserva una clase
      </Link>
      <Link
        className={pathname === "/user/packages" ? "text-primary-text" : ""}
        href="/user/packages"
        onClick={onNavigate}
      >
        Comprar clases regulares
      </Link>
      <Link
        className={pathname === "/user/events" ? "text-primary-text" : ""}
        href="/user/events"
        onClick={onNavigate}
      >
        Tus clases
      </Link>
      <Link
        className={pathname === "/user/profile" ? "text-primary-text" : ""}
        href="/user/profile"
        onClick={onNavigate}
      >
        Perfil
      </Link>
      <span className="cursor-pointer" onClick={() => logUserOut()}>
        Cerrar sesión
      </span>
    </>
  );
}

export default function UserHeader() {
  const { userPack } = useUserStore();

  const [open, setOpen] = useState(false)

  const [notice, setNotice] = useState<
    "hidden" | "upcoming-class" | "upcoming-expiring" | "no-packages"
  >("hidden");

  const [daysLeft, setDaysLeft] = useState(0);

  useEffect(() => {
    setNotice("hidden");

    if (userPack) {
      if (
        userPack.expireDate &&
        +(typeof userPack.expireDate === "string"
          ? new Date(userPack.expireDate)
          : userPack.expireDate) > +new Date(+new Date() + 7 * 24 * 3600 * 1000)
      ) {
        SDK.user.getUserEvents().then((events) => {
          if (
            "events" in events &&
            events.events.length > 0 &&
            +new Date(
              events.events.sort((a: any, b: any) =>
                +(typeof a.start === "string" ? new Date(a.start) : a.start) <
                  +(typeof a.start === "string" ? new Date(b.start) : b.start)
                  ? -1
                  : 1
              )[0].start
            ) <= +new Date(+new Date() + 3 * 24 * 3600 * 1000)
          ) {
            setNotice("upcoming-class");
          }
        });
      } else {
        setNotice("upcoming-expiring");
        setDaysLeft(userPack?.expireDate ? Math.floor((+(typeof userPack.expireDate === "string"
          ? new Date(userPack.expireDate)
          : userPack.expireDate) - +new Date()) / (1000 * 60 * 60 * 24)) : 0);
      }
    } else {
      setNotice("no-packages");
    }
  }, [userPack]);

  return (
    <header className="w-full flex justify-center">
      <div className="w-full max-w-[1440px] p-2">
        <div className="hidden md:flex flex-col w-full max-h-[161px] justify-between items-center gap-4">
          <Image
            alt="Logo"
            width={400}
            height={202}
            src={"/logo.png"}
            className="h-22 w-auto"
          />
          <nav className="flex gap-8 text-md border-t-1 pt-4">
            <Navigation />
          </nav>
        </div>

        <div className="flex md:hidden w-full max-h-[161px] justify-between items-center p-4">
          <Image
            alt="Logo"
            width={400}
            height={202}
            src={"/logo.png"}
            className="h-12 w-auto"
          />

          <div className="mt-2">
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger>
                <Menu stroke="var(--primary-text)" />
              </PopoverTrigger>
              <PopoverContent>
                <nav className="flex flex-col gap-4 text-sm">
                  <Navigation onNavigate={() => setOpen(false)} />
                </nav>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {notice !== "hidden" ? (
          <div className="w-full flex justify-center relative pt-6 pb-2 sm:mt-0">
            <div
              className={
                "relative w-full max-w-[260px] md:max-w-[30%] text-center text-sm p-2 pr-4 font-bold radius-base border-1 border-base-text border-dashed pt-3 " +
                (notice === "upcoming-expiring"
                  ? "bg-secondary-text/85"
                  : "bg-quaternary-bg")
              }
            >
              {notice === "upcoming-expiring" ? (
                <>
                  Tus clases caducarán en {daysLeft} días:
                  <Link
                    href="/user/calendar"
                    title="Reserva ahora tu próxima clase"
                    className="underline block pt-2 text-lg"
                  >
                    Reserva ahora tu próxima clase.
                  </Link>
                </>
              ) : notice === "upcoming-class" ? (
                <>
                  Tu próxima clase está por comenzar:
                  <Link
                    href="/user/events"
                    title="Ver todos los detalles aquí"
                    className="underline block pt-2 text-lg"
                  >
                    Ver todos los detalles aquí.
                  </Link>
                </>
              ) : (
                <>
                  No te queda ninguna clase:
                  <Link
                    href="/user/packages"
                    title="Encuentra aquí un nuevo pack"
                    className="underline block pt-2 text-lg"
                  >
                    Encuentra aquí un nuevo pack.
                  </Link>
                </>
              )}
              <div
                className={
                  "absolute bg-transparent font-bold p-0 m-0 top-1 right-2 cursor-pointer" +
                  (notice === "upcoming-expiring" ? "" : " text-secondary-text")
                }
                onClick={() => {
                  setNotice("hidden");
                }}
              >
                x
              </div>
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    </header>
  );
}
